import { useState } from "react";
import { toast } from "sonner";
import {
  type AddUsersSource,
  AddUsersSourcePicker,
} from "@/features/people/components/add-users-source-picker";
import { AddUsersStepper, type WizardStep } from "@/features/people/components/add-users-stepper";
import { SheetUrlStep, SlackStep } from "@/features/people/components/add-users-steps";
import { ImportPreviewStep } from "@/features/people/components/import-preview-step";
import { PeopleImportDoneNote } from "@/features/people/components/people-import-done-note";
import { useImportGrants } from "@/features/people/hooks/use-import-grants";
import {
  useCommitImport,
  useCsvDryRun,
  useFetchSheetCsv,
} from "@/features/people/hooks/use-imports";
import { warningRowCount } from "@/features/people/lib/dry-run-summary";
import { downloadPeopleSampleCsv } from "@/features/people/lib/sample-csv";
import type { DryRunResult } from "@/features/people/schemas/import.schema";
import { Button } from "@/shared/components/ui/button";
import { DialogDescription, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { mapsBankStatutory } from "@/shared/import/bank-statutory-import-fields";
import type { ColumnMapping } from "@/shared/import/column-mapping";
import { ColumnMappingForm } from "@/shared/import/column-mapping-form";
import { describeImportFailure } from "@/shared/import/import-group-access";
import { CsvUploadStep, ImportDoneStep, type ImportSummary } from "@/shared/import/import-steps";

type Step = "source" | "csv-upload" | "csv-map" | "csv-preview" | "sheet-url" | "slack" | "done";

function wizardStepFor(step: Step): WizardStep {
  if (step === "source") return "source";
  if (step === "done") return "result";
  return "input";
}

type PeopleImportSummary = ImportSummary & { bankUnverified?: boolean; fieldsSkipped?: boolean };

interface Props {
  workspaceId: string;
  onClose: () => void;
  onImported?: () => void;
  onBackToIntro?: () => void;
}

export function AddUsersWizard({ workspaceId, onClose, onImported, onBackToIntro }: Props) {
  const [step, setStep] = useState<Step>("source");
  const [rawCsv, setRawCsv] = useState("");
  const [sheetsUrl, setSheetsUrl] = useState("");
  const [committedSummary, setCommittedSummary] = useState<PeopleImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mapSource, setMapSource] = useState<"csv" | "sheet">("csv");
  // Kept across the preview so Back restores the user's overrides instead of
  // re-suggesting; cleared whenever a new file arrives.
  const [mapping, setMapping] = useState<ColumnMapping | undefined>(undefined);
  const [dryRun, setDryRun] = useState<DryRunResult | null>(null);

  const csvMut = useCsvDryRun(workspaceId);
  const sheetFetchMut = useFetchSheetCsv(workspaceId);
  const commitMut = useCommitImport(workspaceId);
  const importAccess = useImportGrants();
  const downloadSample = () => downloadPeopleSampleCsv(importAccess);
  const importing = csvMut.isPending || sheetFetchMut.isPending || commitMut.isPending;

  function reset() {
    setStep("source");
    setRawCsv("");
    setSheetsUrl("");
    setCommittedSummary(null);
    setError(null);
    setMapSource("csv");
    setMapping(undefined);
    setDryRun(null);
  }

  function pickSource(source: AddUsersSource) {
    if (source === "csv") setStep("csv-upload");
    if (source === "sheet") setStep("sheet-url");
    if (source === "slack") setStep("slack");
  }

  async function handleCsvFile(file: File) {
    const text = await file.text();
    setRawCsv(text);
    setMapping(undefined);
    setMapSource("csv");
    setStep("csv-map");
  }

  async function handleFetchSheet() {
    setError(null);
    try {
      const { csv } = await sheetFetchMut.mutateAsync(sheetsUrl);
      setRawCsv(csv);
      setMapping(undefined);
      setMapSource("sheet");
      setStep("csv-map");
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : "Couldn't fetch the sheet");
    }
  }

  async function runDryRun(transformed: string, confirmedMapping: ColumnMapping) {
    setError(null);
    setMapping(confirmedMapping);
    try {
      setDryRun(await csvMut.mutateAsync(transformed));
      setStep("csv-preview");
    } catch (nextError) {
      setError(describeImportFailure("people", nextError, "Couldn't read the file"));
    }
  }

  // onImported fires only here, after the commit: the onboarding dialog
  // treats it as "the team is in" and moves on to its celebration.
  async function commitDryRun(importId: string, holdInviteEmails: string[]) {
    setError(null);
    try {
      const result = await commitMut.mutateAsync({ importId, holdInviteEmails });
      if (result.status === "failed") {
        setError("The import failed. No rows were written; check the file and try again.");
        return;
      }
      setCommittedSummary({
        created: result.created,
        updated: result.updated,
        errors: result.errors,
        bankUnverified: mapsBankStatutory(mapping),
        fieldsSkipped: dryRun ? warningRowCount(dryRun) > 0 : false,
      });
      setStep("done");
      onImported?.();
      const total = result.created + result.updated;
      toast.success(`Imported ${total} member${total === 1 ? "" : "s"}`);
    } catch (nextError) {
      setError(describeImportFailure("people", nextError, "Import failed"));
    }
  }

  return (
    <>
      <DialogHeader className="shrink-0 items-center border-b border-border p-6 pb-4 text-center">
        <DialogTitle id="add-users-wizard-title" tabIndex={-1}>
          Add users
        </DialogTitle>
        <DialogDescription>
          Bring members into this workspace from a file, a Google Sheet, or Slack.
        </DialogDescription>
        <div className="mt-3">
          <AddUsersStepper active={wizardStepFor(step)} complete={step === "done"} />
        </div>
      </DialogHeader>

      <div className="flex min-h-0 flex-1 flex-col gap-4 p-6">
        {step === "source" ? (
          <AddUsersSourcePicker
            onPick={pickSource}
            onDownloadSample={downloadSample}
            onBackToIntro={onBackToIntro}
          />
        ) : null}

        {step === "csv-upload" ? (
          <CsvUploadStep
            groupKey="people"
            onFile={handleCsvFile}
            onBack={() => setStep("source")}
            onDownloadSample={downloadSample}
          />
        ) : null}

        {step === "csv-map" ? (
          <ColumnMappingForm
            groupKey="people"
            rawCsv={rawCsv}
            pending={importing}
            access={importAccess}
            initialMapping={mapping}
            onBack={() => setStep(mapSource === "sheet" ? "sheet-url" : "csv-upload")}
            onConfirm={runDryRun}
          />
        ) : null}

        {step === "csv-preview" && dryRun ? (
          <ImportPreviewStep
            dry={dryRun}
            pending={commitMut.isPending}
            bankStatutoryMapped={mapsBankStatutory(mapping)}
            onBack={() => {
              setError(null);
              setStep("csv-map");
            }}
            onImport={(holdInviteEmails) => commitDryRun(dryRun.importId, holdInviteEmails)}
          />
        ) : null}

        {step === "sheet-url" ? (
          <SheetUrlStep
            sheetsUrl={sheetsUrl}
            setSheetsUrl={setSheetsUrl}
            pending={importing}
            error={error}
            onBack={() => setStep("source")}
            onSubmit={handleFetchSheet}
          />
        ) : null}

        {step === "slack" ? (
          <SlackStep
            workspaceId={workspaceId}
            onBack={() => setStep("source")}
            onSynced={(result) => {
              setCommittedSummary(result);
              setStep("done");
              onImported?.();
              const total = result.created + result.updated;
              toast.success(`Imported ${total} member${total === 1 ? "" : "s"} from Slack`);
            }}
          />
        ) : null}

        {step === "done" && committedSummary ? (
          <ImportDoneStep
            summary={committedSummary}
            onAddMore={reset}
            onClose={onClose}
            note={<PeopleImportDoneNote {...committedSummary} />}
          />
        ) : null}

        {error && step !== "sheet-url" ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
            <p role="alert" className="text-destructive">
              {error}
            </p>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => setError(null)}>
              Try again
            </Button>
          </div>
        ) : null}
      </div>
    </>
  );
}
